import type { ParsedLogQuery } from '@/components/v1/cloud/logging/log-search/types';
import type { LogLine } from '@/components/v1/cloud/logging/log-search/use-logs';
import {
  compareTimestamps,
  parseTimelineTime,
} from '@/components/v1/cloud/logging/log-timeline';

export type TimelineSource = 'all' | 'events' | 'logs';

function matchesQuery(line: LogLine, query: ParsedLogQuery): boolean {
  const level =
    line.level === 'EVICTION_NOTICE' || line.level === 'RESTORE_NOTICE'
      ? 'info'
      : line.level?.toLowerCase();
  if (query.level && level !== query.level) {
    return false;
  }
  if (query.attempt !== undefined && line.attempt !== query.attempt) {
    return false;
  }
  const text = `${line.line ?? ''}\n${line.error ?? ''}`.toLowerCase();
  return !query.search || text.includes(query.search.toLowerCase());
}

export function mergeRunTimeline(
  events: LogLine[],
  logs: LogLine[],
  hasOlderLogs: boolean,
  source: TimelineSource,
  query: ParsedLogQuery,
): LogLine[] {
  // Logs paginate independently. Do not present older events as a complete
  // timeline until the corresponding log window has been loaded.
  const oldestLogTimestamp = logs.reduce<string | undefined>((oldest, log) => {
    if (!log.timestamp || !parseTimelineTime(log.timestamp)) {
      return oldest;
    }
    return !oldest || (compareTimestamps(log.timestamp, oldest) ?? 0) < 0
      ? log.timestamp
      : oldest;
  }, undefined);
  const visibleEvents =
    source === 'logs'
      ? []
      : events.filter((event) => {
          if (source === 'events' || !hasOlderLogs || !oldestLogTimestamp) {
            return true;
          }
          const comparison = compareTimestamps(
            event.timestamp,
            oldestLogTimestamp,
          );
          return comparison !== undefined && comparison >= 0;
        });
  const visibleLogs = source === 'events' ? [] : logs;
  return [...visibleEvents, ...visibleLogs].filter((line) =>
    matchesQuery(line, query),
  );
}
